import {
  Bot,
  User,
  Wrench,
  Sparkles,
  Loader2,
  Send,
  X,
  History,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '../button';
import type {
  ChatMessage, ToolCategory 
} from './types';
import {
  CATEGORY_CONFIG, SUGGESTED_QUESTIONS 
} from './constants';

interface ChatHeaderProps {
  readonly onClose: () => void;
  readonly onHistoryClick?: () => void;
  readonly onMaximizeClick?: () => void;
  readonly isMaximized?: boolean;
  readonly showHistoryButton?: boolean;
}

export function ChatHeader({
  onClose, onHistoryClick, onMaximizeClick, isMaximized, showHistoryButton 
}: ChatHeaderProps) {
  return (
    <div className={`flex items-center justify-between border-b px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 ${isMaximized ? 'rounded-t-xl' : 'rounded-t-2xl'}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20">
          <Bot className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="font-semibold text-white">GalleyOS Assistant</h3>
          <p className="text-xs text-white/80">Ask me anything about your operations</p>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {showHistoryButton && (
          <button
            type="button"
            onClick={onHistoryClick}
            className="rounded-full p-1.5 text-white/80 hover:bg-white/20 hover:text-white transition-colors"
            aria-label="Conversation history"
          >
            <History className="h-5 w-5" />
          </button>
        )}
        {onMaximizeClick && (
          <button
            type="button"
            onClick={onMaximizeClick}
            className="rounded-full p-1.5 text-white/80 hover:bg-white/20 hover:text-white transition-colors"
            aria-label={isMaximized ? 'Minimize' : 'Maximize'}
          >
            {isMaximized ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          className="rounded-full p-1.5 text-white/80 hover:bg-white/20 hover:text-white transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

interface CategoryBadgesProps {readonly categories: readonly ToolCategory[];}

export function CategoryBadges({ categories }: CategoryBadgesProps) {
  if (categories.length === 0) return null;

  // Filter to only known categories
  const knownCategories = categories.filter((cat) => cat in CATEGORY_CONFIG);
  if (knownCategories.length === 0) return null;

  return (
    <div className="flex items-center gap-1.5 px-4 py-2 border-b bg-muted/30">
      <span className="text-xs text-muted-foreground">Using:</span>
      {knownCategories.map((cat) => {
        const config = CATEGORY_CONFIG[cat];
        const Icon = config.icon;
        return (
          <span
            key={cat}
            className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs"
          >
            <Icon className="h-3 w-3" />
            {config.label}
          </span>
        );
      })}
    </div>
  );
}

interface EmptyStateProps {readonly onSuggestedQuestion: (q: string) => void;}

export function EmptyState({ onSuggestedQuestion }: EmptyStateProps) {
  return (
    <div className="space-y-4">
      <div className="text-center py-6">
        <div className="flex justify-center mb-3">
          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-purple-600" />
          </div>
        </div>
        <h4 className="font-medium text-foreground">How can I help you today?</h4>
        <p className="text-sm text-muted-foreground mt-1">
          I can check inventory, find recipes, track orders, and more.
        </p>
      </div>
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground font-medium">Try asking:</p>
        <div className="flex flex-wrap gap-2">
          {SUGGESTED_QUESTIONS.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => onSuggestedQuestion(q)}
              className="text-xs px-3 py-1.5 rounded-full border bg-background hover:bg-muted transition-colors text-left"
            >
              {q}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

interface MessageBubbleProps {readonly message: ChatMessage;}

export function MessageBubble({ message }: MessageBubbleProps) {
  const isUser = message.role === 'user';

  return (
    <div className={`flex gap-3 ${isUser ? 'justify-end' : 'justify-start'}`}>
      {!isUser && (
        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
          <Bot className="h-4 w-4 text-purple-600" />
        </div>
      )}
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
          isUser ? 'bg-purple-600 text-white rounded-br-md' : 'bg-muted rounded-bl-md'
        }`}
      >
        {message.toolCalls && message.toolCalls.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {message.toolCalls.map((tool) => (
              <span
                key={tool.name}
                className="flex items-center gap-1 px-2 py-0.5 rounded bg-purple-200/50 text-purple-700 text-xs"
              >
                <Wrench className="h-3 w-3" />
                {tool.name.replaceAll('_', ' ')}
              </span>
            ))}
          </div>
        )}
        {isUser ? (
          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
        ) : (
          <div className="text-sm prose prose-sm prose-slate dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-headings:my-2 prose-headings:font-semibold prose-h1:text-base prose-h2:text-sm prose-h3:text-sm prose-table:text-xs prose-th:px-2 prose-th:py-1 prose-td:px-2 prose-td:py-1 prose-pre:bg-slate-800 prose-pre:text-slate-100 prose-code:text-purple-600 prose-code:bg-purple-50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {message.content}
            </ReactMarkdown>
          </div>
        )}
      </div>
      {isUser && (
        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-purple-600 flex items-center justify-center">
          <User className="h-4 w-4 text-white" />
        </div>
      )}
    </div>
  );
}

interface ToolCallIndicatorProps {readonly toolName: string;}

export function ToolCallIndicator({ toolName }: ToolCallIndicatorProps) {
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
        <Bot className="h-4 w-4 text-purple-600" />
      </div>
      <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-2.5">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Using {toolName.replaceAll('_', ' ')}...</span>
        </div>
      </div>
    </div>
  );
}

interface ChatInputProps {
  readonly input: string;
  readonly isLoading: boolean;
  readonly inputRef: React.RefObject<HTMLTextAreaElement | null>;
  readonly onInputChange: (value: string) => void;
  readonly onKeyDown: (e: React.KeyboardEvent) => void;
  readonly onSend: () => void;
}

export function ChatInput({
  input,
  isLoading,
  inputRef,
  onInputChange,
  onKeyDown,
  onSend,
}: ChatInputProps) {
  return (
    <div className="border-t p-4">
      <div className="flex gap-2">
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask about inventory, recipes, orders..."
          className="flex-1 resize-none rounded-xl border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[44px] max-h-[120px]"
          rows={1}
          disabled={isLoading}
        />
        <Button
          onClick={onSend}
          disabled={input.trim() === '' || isLoading}
          className="h-11 w-11 rounded-xl bg-purple-600 hover:bg-purple-700 p-0"
        >
          {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mt-2 text-center">Powered by Amazon Bedrock</p>
    </div>
  );
}
